import React, { useState } from "react";
import { Quote, Play, Star, Images, X } from "lucide-react";
import { TESTIMONIALS, VIDEO_TESTIMONIALS, SCREENSHOTS } from "../mock";
import { useReveal } from "../hooks/useReveal";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";

function TextCard({ t, i }) {
  const ref = useReveal();
  return (
    <div ref={ref} className={`reveal reveal-delay-${(i % 3) + 1} glass card-lift rounded-2xl p-7 flex flex-col`}>
      <Quote className="w-8 h-8 text-[#f6e3a8]/60 mb-3" />
      <p className="text-white/80 leading-relaxed text-[15px] flex-1">“{t.quote}”</p>
      <div className="mt-6 flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#1f6f8b] to-[#5a5ee0] flex items-center justify-center text-white font-semibold">
          {t.name.charAt(0)}
        </div>
        <div>
          <div className="text-white font-medium text-sm">
            {t.name}{t.age ? ` – ${t.age}` : ""}
          </div>
          <div className="text-white/50 text-xs">{t.role}</div>
        </div>
        <div className="ml-auto flex gap-0.5">
          {[0, 1, 2, 3, 4].map((s) => (
            <Star key={s} className="w-3 h-3 text-[#f6e3a8] fill-[#f6e3a8]" />
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Testimonials({
  items = TESTIMONIALS,
  videos = VIDEO_TESTIMONIALS,
  screenshots = SCREENSHOTS,
}) {
  const head = useReveal();
  const vHead = useReveal();
  const sHead = useReveal();
  const [video, setVideo] = useState(null);
  const [shot, setShot] = useState(null);

  return (
    <section id="temoignages" className="relative py-24 md:py-28">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div ref={head} className="reveal text-center max-w-3xl mx-auto">
          <p className="eyebrow text-[#7fd8e8] text-xs mb-4">Témoignages</p>
          <h2 className="text-4xl md:text-5xl text-white">Ils ont vécu <span className="text-gold">l'expérience</span></h2>
        </div>
        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((t, i) => (
            <TextCard key={t.name} t={t} i={i} />
          ))}
        </div>

        {/* Video testimonials */}
        <div ref={vHead} className="reveal text-center max-w-3xl mx-auto mt-24">
          <p className="eyebrow text-[#7fd8e8] text-xs mb-4">Témoignages vidéo</p>
          <h2 className="text-4xl md:text-5xl text-white">Découvre leurs <span className="text-gold">transformations</span></h2>
        </div>
        <div className="mt-12 grid md:grid-cols-3 gap-6">
          {videos.map((v, i) => (
            <VideoCard key={v.name} v={v} i={i} onPlay={() => setVideo(v)} />
          ))}
        </div>

        {/* Screenshots */}
        <div ref={sHead} className="reveal flex items-center justify-center gap-2 mt-24 mb-10">
          <Images className="w-5 h-5 text-[#7fd8e8]" />
          <p className="eyebrow text-white/55 text-xs">Leurs messages, en direct</p>
        </div>
        <div className="columns-2 md:columns-3 gap-4 [column-fill:_balance]">
          {screenshots.map((src, i) => (
            <ShotThumb key={i} src={src} onClick={() => setShot(src)} />
          ))}
        </div>
      </div>

      {/* Video modal */}
      <Dialog open={!!video} onOpenChange={(o) => !o && setVideo(null)}>
        <DialogContent
          className={`bg-[#070d1c] border-white/10 p-0 overflow-hidden ${
            video?.orientation === "portrait" ? "max-w-sm w-[92vw]" : "max-w-3xl w-[92vw]"
          }`}
        >
          <DialogTitle className="sr-only">{video?.name}</DialogTitle>
          {video && (
            <div className={video.orientation === "portrait" ? "aspect-[9/16] w-full" : "aspect-video w-full"}>
              <iframe
                className="w-full h-full"
                src={`https://www.youtube.com/embed/${video.youtubeId}?autoplay=1&rel=0&playsinline=1`}
                title={video.name}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          )}
          <div className="px-5 py-4">
            <p className="text-white font-medium">{video?.name}</p>
            <p className="text-white/50 text-sm">{video?.role}</p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Screenshot lightbox */}
      <Dialog open={!!shot} onOpenChange={(o) => !o && setShot(null)}>
        <DialogContent className="max-w-2xl w-[92vw] bg-[#070d1c] border-white/10 p-3">
          <DialogTitle className="sr-only">Témoignage écran</DialogTitle>
          {shot && <img src={shot} alt="témoignage" className="w-full rounded-lg" />}
        </DialogContent>
      </Dialog>
    </section>
  );
}

function VideoCard({ v, i, onPlay }) {
  const ref = useReveal();
  return (
    <button
      ref={ref}
      onClick={onPlay}
      className={`reveal reveal-delay-${i + 1} group relative rounded-2xl overflow-hidden glass card-lift text-left`}
    >
      <div className="relative h-64 overflow-hidden">
        <img
          src={v.thumb}
          alt={v.name}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#070d1c] via-transparent to-transparent" />
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="w-16 h-16 rounded-full btn-gold flex items-center justify-center group-hover:scale-110 transition-transform">
            <Play className="w-6 h-6 ml-1 fill-[#10192c] text-[#10192c]" />
          </span>
        </div>
      </div>
      <div className="p-5">
        <p className="text-white font-medium">{v.name}</p>
        <p className="text-white/50 text-sm">{v.role}</p>
      </div>
    </button>
  );
}

function ShotThumb({ src, onClick }) {
  return (
    <button
      onClick={onClick}
      className="mb-4 block w-full overflow-hidden rounded-xl ring-1 ring-white/10 hover:ring-[#f6e3a8]/50 transition-all group"
    >
      <img
        src={src}
        alt="témoignage écran"
        className="w-full transition-transform duration-500 group-hover:scale-105"
        loading="lazy"
      />
    </button>
  );
}
